/*
 * Handing over from the server-rendered snapshot to React.
 *
 * On Netlify the SEO Edge Function (netlify/edge-functions/seo.js) puts the
 * page's pre-rendered HTML inside <div id="root" data-mxc-snapshot="200"> and
 * the shop's theme in <style id="mxc-theme">. createRoot would wipe that HTML
 * on its first commit and show skeletons while the page loads its data. So:
 *
 *  1. startSnapshotHandoff() moves the snapshot out of #root into a sibling
 *     (#mxc-snapshot) and hides #root; the visitor keeps reading the snapshot.
 *  2. React renders into the hidden #root. When the page has its data
 *     (utils/usePageReady), revealApp() removes the snapshot and shows React
 *     in the same frame: one swap, no skeleton, no flash of the default theme.
 *  3. A timeout reveals React anyway, so a page that never reports ready can
 *     never stay hidden.
 *
 * Without a snapshot (demo mode, API down, client-only routes) all of this is
 * a no-op.
 */

import { pageKey } from './canonical';

const REVEAL_TIMEOUT_MS = 6000;

// The server-rendered snapshot (title, description, share tags, body) is
// always Indonesian. React replaces its body whatever the visitor's language.
export const SNAPSHOT_LANGUAGE = 'id';

let pending = null; // { holder, root, timer }
let snapshotPath = null; // the page (path + ?halaman=N) whose <head> came from the snapshot

/** @returns {boolean} whether a snapshot was found and is being shown */
export function startSnapshotHandoff(root, { timeoutMs = REVEAL_TIMEOUT_MS } = {}) {
  if (!root || !root.hasAttribute('data-mxc-snapshot') || !root.firstChild) return false;
  const holder = document.createElement('div');
  holder.id = 'mxc-snapshot';
  while (root.firstChild) holder.appendChild(root.firstChild);
  root.parentNode.insertBefore(holder, root);
  root.removeAttribute('data-mxc-snapshot');
  root.hidden = true;
  snapshotPath = pageKey(window.location.pathname, window.location.search);
  pending = { holder, root, timer: setTimeout(revealApp, timeoutMs) };
  return true;
}

/** Show React and drop the snapshot (idempotent). */
export function revealApp() {
  if (!pending) return;
  const { holder, root, timer } = pending;
  pending = null;
  clearTimeout(timer);
  holder.remove();
  root.hidden = false;
}

export const isHandoffPending = () => pending !== null;

/**
 * Is the document <head> still the snapshot's, made for this very page
 * (`pageKey`: the path, plus ?halaman=N on page 2+) and in the language the
 * visitor reads? Then the title, description and share tags
 * are already right and must not be replaced by a placeholder while the page
 * loads. Becomes false for good after the first client-side navigation, and as
 * soon as the UI language is not the snapshot's (the snapshot's head is
 * Indonesian; the page then sets its own, translated title).
 */
export function snapshotHeadIsCurrent(pathname, language = SNAPSHOT_LANGUAGE) {
  if (snapshotPath === null) return false;
  if (pathname === snapshotPath && language === SNAPSHOT_LANGUAGE) return true;
  snapshotPath = null;
  return false;
}

/** Whether the Edge Function inlined the shop's theme into this page. */
export const hasServerTheme = () =>
  typeof document !== 'undefined' && Boolean(document.getElementById('mxc-theme'));

// For tests.
export function resetSnapshotHandoff() {
  if (pending) clearTimeout(pending.timer);
  pending = null;
  snapshotPath = null;
}
